// Phase 29.4 · Secondary Order — retailer-ranked SKU list, 3-unit picker,
// real-time scheme panel, strict-stock hide/block, PDF + email share.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, CheckCircle2, Loader2,
  Package, Search, ShoppingCart, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import OrderShareButtons from "@/components/orders/OrderShareButtons";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const INR = new Intl.NumberFormat("en-IN", {
  style: "currency", currency: "INR", maximumFractionDigits: 0,
});

// Mirrors backend routes_uoms.py::convert_to_canonical — client-side only,
// for the live "add X more to unlock this scheme" gap hint below. The
// server re-derives this authoritatively at preview/submit time.
function toCanonical(sku, uom, enteredQty) {
  const q = Number(enteredQty) || 0;
  if (!sku || q <= 0) return { qtyPcs: 0, qtyKg: 0 };
  if (uom === "KG") {
    const w = sku.weight_per_pc_kg;
    if (!w) return { qtyPcs: 0, qtyKg: 0 };
    return { qtyPcs: Math.round(q / w), qtyKg: q };
  }
  if (uom === "BAG") {
    const f = sku.bag_conversion_factor;
    if (!f) return { qtyPcs: 0, qtyKg: 0 };
    const qtyPcs = Math.round(q * f);
    return { qtyPcs, qtyKg: qtyPcs * (sku.weight_per_pc_kg || 0) };
  }
  if (uom === "BUNCH") {
    const f = sku.bunch_conversion_factor;
    if (!f) return { qtyPcs: 0, qtyKg: 0 };
    const qtyPcs = Math.round(q * f);
    return { qtyPcs, qtyKg: qtyPcs * (sku.weight_per_pc_kg || 0) };
  }
  const qtyPcs = Math.round(q);
  return { qtyPcs, qtyKg: qtyPcs * (sku.weight_per_pc_kg || 0) };
}

// Same reward-at-current-qty math as PrimaryOrder.jsx's _rewardForHint —
// kept in sync with backend routes_orders.py::_apply_scheme_to_line's
// FREE_GOODS multiple-of-buy_qty logic.
function _rewardForHint(hint, qtyKg, qtyPcs) {
  if (!hint || !hint.kind) return null;
  const threshold = hint.buy_qty_kg > 0 ? hint.buy_qty_kg : hint.buy_qty_pcs;
  const have = hint.buy_qty_kg > 0 ? qtyKg : qtyPcs;
  if (!threshold || !have) return null;
  const mult = Math.floor(have / threshold);
  if (mult <= 0) return null;
  if (hint.kind === "discount") {
    const amt = mult * (hint.amount_per_unit || 0);
    return { mult, kind: hint.kind, scheme_id: hint.scheme_id, scheme_code: hint.scheme_code,
             text: `₹${amt.toLocaleString("en-IN")} off` };
  }
  if (hint.kind === "free_item_note" && hint.unit_has_number === false) {
    return { mult, kind: hint.kind, scheme_id: hint.scheme_id, scheme_code: hint.scheme_code,
             text: mult > 1 ? `${mult} × ${hint.unit_label || ""}` : (hint.unit_label || "") };
  }
  const qty = mult * (hint.unit_qty || 0);
  const qtyStr = Number.isInteger(qty) ? String(qty) : qty.toFixed(2).replace(/\.?0+$/, "");
  return { mult, kind: hint.kind, scheme_id: hint.scheme_id, scheme_code: hint.scheme_code,
           text: `${qtyStr} ${hint.unit_label || ""}`.trim() };
}

// All live rewards a SKU has earned at its current entered quantity — one
// entry per matching scheme hint.
function liveSchemeRewards(sku, qtyKg, qtyPcs) {
  return (sku.scheme_hints || []).map((h) => _rewardForHint(h, qtyKg, qtyPcs)).filter(Boolean);
}

// "Add N more kg to unlock the next tier" — shown whether or not the
// current qty has already crossed a threshold, same idea as PrimaryOrder's
// nextTierGaps, adapted to Secondary Order's KG/BAG/BUNCH-only entry.
function nextTierGaps(sku, qtyKg, qtyPcs) {
  const out = [];
  for (const hint of sku.scheme_hints || []) {
    const kgBased = hint.buy_qty_kg > 0;
    const threshold = kgBased ? hint.buy_qty_kg : hint.buy_qty_pcs;
    if (!threshold) continue;
    const have = kgBased ? (qtyKg || 0) : (qtyPcs || 0);
    const mult = Math.floor(have / threshold);
    const remainder = have - mult * threshold;
    if (remainder <= 0) continue; // already exactly on a tier boundary
    const gap = Math.round((threshold - remainder) * 100) / 100;
    const nextReward = _rewardForHint(
      hint,
      kgBased ? qtyKg + gap : qtyKg,
      kgBased ? qtyPcs : qtyPcs + gap,
    );
    if (!nextReward) continue;
    out.push({ gap, unit: kgBased ? "kg" : "pcs", reward: nextReward });
  }
  return out;
}

export default function SecondaryOrder() {
  const { retailer_id } = useParams();
  const [params] = useSearchParams();
  const visit_id = params.get("visit_id");
  const nav = useNavigate();
  const { user } = useAuth();
  const isDistributor = user?.role_code === "DISTRIBUTOR";
  const beatHomePath = isDistributor ? "/portal/beat" : "/today-beat";

  const [skus, setSKUs] = useState([]);
  // Every SKU seen across searches/filters. `skus` is replaced on each
  // search/category change while typed quantities (`entries`) persist, so the
  // review step looked a line's SKU up in a list that no longer contained it
  // and rendered a blank code/name.
  const [knownSkus, setKnownSkus] = useState({});
  const [context, setContext] = useState(null);   // Enhancement 7 header
  const [q, setQ] = useState("");
  // Category/variant filter — beside the search box. Options come from the
  // same /orderable-skus response (`filters` block), no extra round-trip.
  const [category, setCategory] = useState("");
  const [variant, setVariant] = useState("");
  const [filterOptions, setFilterOptions] = useState({ categories: [], variants: [] });
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState({}); // sku_id -> {qty, uom}
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savedOrder, setSavedOrder] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const debounceRef = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/retailers/${retailer_id}/orderable-skus`,
        { params: { ...(q ? { q } : {}), ...(category ? { category } : {}),
                    ...(variant ? { variant } : {}) } });
      setSKUs(data.items || []);
      setKnownSkus((prev) => {
        const next = { ...prev };
        for (const it of data.items || []) next[it.sku_id] = it;
        return next;
      });
      if (data.context) setContext(data.context);
      if (data.filters) setFilterOptions(data.filters);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [retailer_id, q, category, variant]);

  useEffect(() => { load(); }, [load]);

  const lines = useMemo(() =>
    Object.entries(entries)
      .filter(([, e]) => Number(e.qty) > 0)
      .map(([sku_id, e]) => ({ sku_id, qty_pcs: Number(e.qty), uom: e.uom })),
    [entries]);

  // Drop 5C (2026-02): client-side over-qty guard.
  // Only enforce when UOM=PCS because `available_qty_pcs` is in pcs and
  // conversion from KG/BAG happens server-side. Approximate guard prevents
  // the most common data-entry mistake (typing over the ceiling) without
  // duplicating UOM math on the client.
  const skuById = useMemo(
    () => ({ ...knownSkus, ...Object.fromEntries(skus.map((s) => [s.sku_id, s])) }),
    [skus, knownSkus]);
  // Per-SKU warning from the live preview (e.g. a KG/BAG entry that rounds
  // down to 0 pieces because it's smaller than one pack) — surfaced right
  // on the entry row instead of silently zeroing the total with no
  // explanation (see scheme_preview in routes_phase29_4.py).
  const previewWarningBySkuId = useMemo(
    () => Object.fromEntries((preview?.lines || [])
      .filter((l) => l.warning)
      .map((l) => [l.sku_id, l.warning])),
    [preview]);
  const overQtySet = useMemo(() => {
    const bad = new Set();
    for (const [sku_id, e] of Object.entries(entries)) {
      const qty = Number(e.qty) || 0;
      if (qty <= 0) continue;
      const uom = (e.uom || "PCS").toUpperCase();
      if (uom !== "PCS") continue;
      const avail = skuById[sku_id]?.available_qty_pcs;
      if (typeof avail === "number" && qty > avail) bad.add(sku_id);
    }
    return bad;
  }, [entries, skuById]);
  const hasOverQty = overQtySet.size > 0;

  // Drop 5C (2026-02): order total from live scheme preview.
  // gross − scheme_discount = net-before-tax; tax is computed server-side
  // on POST /orders. We intentionally show gross-minus-discount (not net
  // final) to avoid drifting from the server total when tax is present —
  // the SE just needs a directional total for confirmation.
  const orderTotal = useMemo(() => {
    if (!preview?.totals) return 0;
    const g = Number(preview.totals.gross || 0);
    const d = Number(preview.totals.scheme_discount || 0);
    return Math.max(0, g - d);
  }, [preview]);

  // Debounced scheme preview.
  useEffect(() => {
    if (lines.length === 0) { setPreview(null); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        // Hotfix — this used to send `qty_pcs: l.qty_pcs`, which is really
        // just the raw number typed (l.qty_pcs is misleadingly named — see
        // the `lines` useMemo above), always treated as a literal piece
        // count by the preview regardless of the KG/BAG toggle. Send
        // entered_uom/entered_qty instead, same shape the real booking
        // payload already uses, so the live total actually reflects the
        // selected unit.
        const { data } = await api.post("/orders/scheme-preview", {
          retailer_id, lines: lines.map((l) =>
            ({ sku_id: l.sku_id, entered_uom: l.uom || "KG", entered_qty: l.qty_pcs })),
        });
        setPreview(data);
      } catch (e) { /* silent — preview is advisory */ }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [retailer_id, lines]);

  const updateEntry = (sku_id, field, val) => {
    // Order entry: Kg / Bag / Bunch only — default to the SKU's first
    // allowed non-PCS unit rather than PCS.
    const allowed = (skuById[sku_id]?.allowed_uoms || []).filter(
      (u) => ["KG", "BAG", "BUNCH"].includes(u));
    setEntries((prev) => ({
      ...prev,
      [sku_id]: { qty: "", uom: allowed[0] || "KG", ...prev[sku_id], [field]: val },
    }));
  };

  const submit = async () => {
    if (lines.length === 0) return toast.error("Add at least one line");
    if (hasOverQty) return toast.error(
      "One or more lines exceed available stock. Adjust quantities first.");
    setBusy(true);
    try {
      const body = {
        retailer_id,
        visit_id,
        lines: lines.map((l) => ({
          sku_id: l.sku_id,
          entered_uom: l.uom || "KG",
          entered_qty: l.qty_pcs,
        })),
        idempotency_key: (crypto.randomUUID?.()
                            || `secord-${retailer_id}-${Date.now()}`),
      };
      const { data } = await api.post("/orders", body);
      // Snapshot the kg/free-item totals from `preview` at the moment of
      // success — the success card needs them, but `preview`/`entries`
      // aren't guaranteed to stay untouched afterward (e.g. a stray re-run
      // of the debounced preview effect).
      setSavedOrder({
        ...data,
        total_qty_kg: preview?.totals?.qty_kg ?? 0,
        free_item_totals: freeItemTotals(preview?.lines || []),
      });
      setShowReview(false);
      toast.success(`Order ${data.order_no} booked`);
      // Activity forms of this outlet call open straight away (after the order, before the
      // call is completed) so they are not forgotten.
      if (visit_id) {
        try {
          const f = await api.get(`/activity-forms/for-visit/${visit_id}`);
          if ((f.data.items || []).length > 0) {
            nav(`/field/call-forms/${visit_id}`, { replace: true, state: { orderNo: data.order_no, orderId: data.id } });
            return;
          }
        } catch { /* no forms / offline: stay on the success card */ }
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error === "insufficient_stock") {
        toast.error(
          `Only ${d.available_qty_kg ?? 0} kg of ${d.sku_code} available.`);
      } else { toast.error(formatApiError(e)); }
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 pb-24 space-y-3" data-testid="secondary-order-screen">
      <button onClick={() => nav(-1)}
              className="flex items-center gap-1 text-xs text-neutral-500"
              data-testid="secord-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400">
          Secondary Order
        </div>
        <h1 className="text-xl font-extrabold text-neutral-900">
          {savedOrder ? `Booked ${savedOrder.order_no}` : "Add lines"}
        </h1>
      </div>

      {/* Enhancement 7 (2026-02): retailer / distributor / beat context */}
      {context && (
        <Card className="border-neutral-200 rounded-2xl"
              data-testid="secord-context">
          <CardContent className="p-3 space-y-1">
            <div className="text-sm font-semibold text-neutral-900"
                 data-testid="secord-context-retailer">
              {context.retailer_name || "—"}
              {context.retailer_code && (
                <span className="ml-2 text-[11px] font-normal text-neutral-400">
                  {context.retailer_code}
                </span>
              )}
            </div>
            {(context.owner_name || context.owner_mobile) && (
              <div className="text-[11px] text-neutral-700 flex flex-wrap gap-x-3 gap-y-0.5">
                {context.owner_name && (
                  <span data-testid="secord-context-owner-name">
                    <span className="text-neutral-400">Owner · </span>
                    {context.owner_name}
                  </span>
                )}
                {context.owner_mobile && (
                  <a href={`tel:${context.owner_mobile}`}
                     className="text-[#6D2C91] font-medium underline decoration-dotted"
                     data-testid="secord-context-owner-mobile">
                    <span className="text-neutral-400 no-underline">Mobile · </span>
                    {context.owner_mobile}
                  </a>
                )}
              </div>
            )}
            <div className="text-[11px] text-neutral-600 flex flex-wrap gap-x-3">
              <span data-testid="secord-context-distributor">
                <span className="text-neutral-400">Distributor · </span>
                {context.distributor_name || "—"}
              </span>
              <span data-testid="secord-context-beat">
                <span className="text-neutral-400">Beat · </span>
                {context.beat_name || "—"}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {!savedOrder && (
        <>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-3 text-neutral-400" />
            <Input placeholder="Search SKUs…" value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="pl-9"
                    data-testid="secord-search" />
          </div>
          <div className="flex gap-2">
            <Select value={category || "__all__"}
                    onValueChange={(v) => setCategory(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-9 text-xs flex-1" data-testid="secord-filter-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All categories</SelectItem>
                {filterOptions.categories.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.label || c.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={variant || "__all__"}
                    onValueChange={(v) => setVariant(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-9 text-xs flex-1" data-testid="secord-filter-variant">
                <SelectValue placeholder="Variant" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All variants</SelectItem>
                {filterOptions.variants.map((v) => (
                  <SelectItem key={v.code} value={v.code}>{v.label || v.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loading && <Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" />}
          {!loading && skus.length === 0 && (
            <div className="py-8 text-center text-sm text-neutral-500"
                 data-testid="secord-empty">
              No SKUs for this distributor yet.
            </div>
          )}

          <div className="space-y-2">
            {skus.map((s) => (
              <SkuRow key={s.sku_id} s={s}
                      entry={entries[s.sku_id]}
                      overqty={overQtySet.has(s.sku_id)}
                      warning={previewWarningBySkuId[s.sku_id]}
                      onQty={(v) => updateEntry(s.sku_id, "qty", v)}
                      onUom={(v) => updateEntry(s.sku_id, "uom", v)} />
            ))}
          </div>

          {preview && preview.lines?.length > 0 && (
            <>
              <BasketRollup preview={preview} />
              <SchemePanel preview={preview} />
            </>
          )}

          <div className="fixed bottom-16 left-4 right-4 z-30 space-y-2">
            {hasOverQty && (
              <div className="rounded-lg bg-amber-50 border border-amber-300
                              px-3 py-2 text-[11px] text-amber-900 flex items-center gap-2"
                   data-testid="secord-overqty-banner">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                {overQtySet.size} line{overQtySet.size === 1 ? "" : "s"} exceed
                {overQtySet.size === 1 ? "s" : ""} available stock. Adjust to continue.
              </div>
            )}
            <div className="flex items-center justify-between rounded-xl bg-white
                            border border-neutral-200 px-3 py-2 shadow-sm">
              <div className="text-[11px] text-neutral-500 leading-tight">
                <div>
                  <span data-testid="secord-order-item-count">{lines.length}</span>
                  {" "}line{lines.length === 1 ? "" : "s"}
                  {" · "}
                  <span data-testid="secord-total-kg">
                    {(preview?.totals?.qty_kg ?? 0).toFixed(2)} kg
                  </span>
                </div>
                <div className="text-sm font-bold text-neutral-900"
                     data-testid="secord-order-total">
                  {INR.format(orderTotal)}
                </div>
              </div>
              <Button onClick={() => setShowReview(true)}
                      disabled={busy || lines.length === 0 || hasOverQty}
                      aria-disabled={busy || lines.length === 0 || hasOverQty}
                      className="h-12 px-5 rounded-xl tulsi-gradient text-white text-base
                                 disabled:opacity-50 disabled:cursor-not-allowed
                                 disabled:pointer-events-none disabled:grayscale"
                      data-testid="secord-place-order">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      : <ShoppingCart className="h-4 w-4 mr-2" />}
                Next — Review
              </Button>
            </div>
          </div>
        </>
      )}

      {savedOrder && (
        <Card className="border-green-200 bg-green-50 rounded-2xl"
              data-testid="secord-success">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-green-800 font-semibold">
              <CheckCircle2 className="h-5 w-5" /> Order booked
            </div>
            <div className="text-xs text-green-800">
              {savedOrder.order_no} · {INR.format(savedOrder.net_amount || 0)}
              {" · "}<span data-testid="secord-success-total-kg">{(savedOrder.total_qty_kg || 0).toFixed(2)} kg</span>
            </div>
            {savedOrder.scheme_discount_amount > 0 && (
              <div className="text-xs text-green-800" data-testid="secord-success-scheme-discount">
                Scheme discount: {INR.format(savedOrder.scheme_discount_amount)}
              </div>
            )}
            {(savedOrder.free_item_totals || []).map(([label, count]) => (
              <div key={label} className="text-xs text-green-800"
                   data-testid={`secord-success-free-item-total-${label.replace(/\W+/g, "-")}`}>
                🎁 Total {label}: {count}
              </div>
            ))}
            <OrderShareButtons order={savedOrder} />
            <Button onClick={() => nav(beatHomePath, { replace: true })}
                    variant="outline"
                    className="w-full h-10 rounded-lg border-green-300 text-green-800 hover:bg-green-100"
                    data-testid="secord-goto-today-beat">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {isDistributor ? "Back to Beat" : "Back to Today's Beat"}
            </Button>
          </CardContent>
        </Card>
      )}


      {/* Review order — a confirm step between entry and submit so the rep
          can double-check quantities and see every scheme benefit (including
          non-inventory free-item giveaways, which the entry-screen panel
          only shows a compact line for) in one place before it's booked. */}
      <Dialog open={showReview} onOpenChange={setShowReview}>
        <DialogContent className="max-w-md w-[calc(100vw-1.5rem)] max-h-[85vh] overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)] [&>*]:min-w-0" data-testid="secord-review-dialog">
          <DialogHeader>
            <DialogTitle>Review order</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-neutral-500">
            {context?.retailer_name || "Retailer"}
            {context?.retailer_code && <span className="ml-1">({context.retailer_code})</span>}
          </div>
          <div className="border rounded-lg divide-y divide-neutral-100">
            {(preview?.lines || []).map((l) => {
              const sku = skuById[l.sku_id];
              const fired = lineFired(l);
              return (
                <div key={l.sku_id} className="p-2 text-sm" data-testid={`secord-review-line-${l.sku_id.slice(0, 8)}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 truncate">
                      <span className="font-mono text-[11px] text-neutral-500 mr-1.5">{sku?.sku_code}</span>
                      <span className="text-neutral-900">{sku?.name || "—"}</span>
                    </div>
                    <div className="text-right shrink-0 whitespace-nowrap">
                      <div>{l.qty_kg.toFixed(1)} kg</div>
                      <div className="text-[11px] text-neutral-500">{INR.format(l.gross)}</div>
                    </div>
                  </div>
                  {fired && (
                    <div className="text-[11px] text-emerald-700 font-semibold mt-0.5 truncate"
                         data-testid={`secord-review-reward-${l.sku_id.slice(0, 8)}`}>
                      🎁 {l.benefit_type === "free_item_note" ? l.benefit_value
                        : l.free_qty_kg > 0 ? `${l.free_qty_kg} kg free`
                        : `${INR.format(l.discount_amount)} off`}
                    </div>
                  )}
                  {l.warning && (
                    <div className="text-[11px] text-amber-700 mt-0.5"
                         data-testid={`secord-review-warning-${l.sku_id.slice(0, 8)}`}>
                      ⚠️ {l.warning}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="rounded-lg border border-neutral-200 px-3 py-2 space-y-1 text-xs">
            <div className="flex items-center justify-between gap-2 text-neutral-500">
              <span className="shrink-0">{lines.length} line{lines.length === 1 ? "" : "s"}</span>
              <span className="shrink-0">Total Kg</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="font-bold text-neutral-900 shrink-0 whitespace-nowrap" data-testid="secord-review-total">
                {INR.format(orderTotal)}
              </span>
              <span className="font-semibold text-neutral-900 shrink-0 whitespace-nowrap" data-testid="secord-review-total-kg">
                {(preview?.totals?.qty_kg ?? 0).toFixed(2)} kg
              </span>
            </div>
            {freeItemTotals(preview?.lines || []).map(([label, count]) => (
              <div key={label} className="flex items-center justify-between gap-2 text-emerald-700 font-semibold pt-1"
                   data-testid={`secord-review-free-item-total-${label.replace(/\W+/g, "-")}`}>
                <span className="min-w-0 truncate">🎁 Total {label}</span>
                <span className="shrink-0">{count}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowReview(false)}
                    className="flex-1 h-11 rounded-lg border border-neutral-200 text-sm font-medium"
                    data-testid="secord-review-back-btn">
              Back
            </button>
            <button onClick={submit} disabled={busy}
                    className="flex-1 h-11 rounded-lg tulsi-gradient text-white font-medium text-sm disabled:opacity-50"
                    data-testid="secord-review-confirm-btn">
              {busy ? <Loader2 className="w-5 h-5 mx-auto animate-spin" /> : "Confirm & place order"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SkuRow({ s, entry, overqty, warning, onQty, onUom }) {
  // Order entry: Kg / Bag / Bunch only — PCS excluded.
  const allowed = (s.allowed_uoms || []).filter(
    (u) => ["KG", "BAG", "BUNCH"].includes(u));
  const uom = entry?.uom || allowed[0] || "KG";
  const { qtyKg, qtyPcs } = toCanonical(s, uom, entry?.qty);
  const hasQty = Number(entry?.qty) > 0;
  const rewards = hasQty ? liveSchemeRewards(s, qtyKg, qtyPcs) : [];
  const gapHints = hasQty ? nextTierGaps(s, qtyKg, qtyPcs) : [];
  return (
    <Card className={"rounded-2xl "
            + (overqty ? "border-amber-300 bg-amber-50" : "border-neutral-200")}
          data-testid={`secord-row-${s.sku_id.slice(0, 8)}`}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start gap-2">
          <Package className="h-4 w-4 mt-0.5 text-neutral-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] text-neutral-400">{s.sku_code}</div>
            <div className="text-sm font-semibold text-neutral-900 truncate">
              {s.name}
            </div>
            <div className="text-[11px] text-neutral-500 mt-0.5 flex items-center gap-2 flex-wrap">
              <span>Avail: <b>{((s.available_qty_pcs || 0) * (s.weight_per_pc_kg || 0)).toFixed(2)}</b> kg</span>
              {s.retailer_qty_pcs_6mo > 0 && (
                <span className="text-[#E5197F]">
                  · Retailer 6mo: <b>{(s.retailer_qty_pcs_6mo * (s.weight_per_pc_kg || 0)).toFixed(2)}</b> kg
                  {" "}(avg <b>{(s.retailer_qty_pcs_6mo * (s.weight_per_pc_kg || 0) / 6).toFixed(2)}</b> kg/mo)
                </span>
              )}
              {overqty && (
                <span className="inline-flex items-center gap-1 rounded-full
                                 bg-amber-200 text-amber-900 px-2 py-0.5 text-[10px]
                                 font-semibold"
                      data-testid={`secord-line-overqty-badge-${s.sku_id.slice(0, 8)}`}>
                  <AlertTriangle className="h-3 w-3" /> Over available
                </span>
              )}
            </div>
            {(s.scheme_hints || []).map((h, i) => (
              <div key={h.scheme_id || i} className="text-[10px] text-[#E5197F] font-semibold mt-0.5"
                   data-testid={`secord-scheme-hint-${s.sku_id.slice(0, 8)}-${i}`}>
                🎁 Scheme: {h.text}
              </div>
            ))}
            {rewards.map((r, i) => (
              <div key={r.scheme_id || i} className="text-[10px] text-emerald-700 font-semibold mt-0.5"
                   data-testid={`secord-scheme-eligible-${s.sku_id.slice(0, 8)}-${i}`}>
                ✓ Eligible now: {r.kind === "discount" ? r.text : `${r.text} free`}
              </div>
            ))}
            {gapHints.map((g, i) => (
              <div key={i} className="text-[10px] text-amber-600 font-semibold mt-0.5"
                   data-testid={`secord-scheme-gap-${s.sku_id.slice(0, 8)}-${i}`}>
                ➕ Add {g.gap} {g.unit} more for{" "}
                {g.reward.text}{g.reward.kind !== "discount" ? " free" : ""}
              </div>
            ))}
          </div>
        </div>
        {allowed.length === 0 ? (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200
                          rounded px-2 py-1.5"
               data-testid={`secord-no-uom-${s.sku_id.slice(0, 8)}`}>
            No Kg/Bag/Bunch unit configured for this SKU — ask admin to enable one.
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Input type="number" min={0} max={s.available_qty_pcs}
                   placeholder="Qty"
                   value={entry?.qty || ""}
                   onChange={(e) => onQty(e.target.value)}
                   className="w-24 h-9"
                   data-testid={`secord-qty-${s.sku_id.slice(0, 8)}`} />
            {allowed.length > 1 ? (
              <div className="flex rounded-lg border border-neutral-200 overflow-hidden">
                {allowed.map((u) => (
                  <button key={u}
                          onClick={() => onUom(u)}
                          data-testid={`secord-uom-${s.sku_id.slice(0, 8)}-${u.toLowerCase()}`}
                          className={"px-2 h-9 text-xs "
                            + (uom === u
                                ? "bg-neutral-900 text-white"
                                : "bg-white text-neutral-600")}>
                    {u}
                  </button>
                ))}
              </div>
            ) : (
              <span className="text-xs text-neutral-500 px-2">{uom}</span>
            )}
          </div>
        )}
        {warning && (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200
                          rounded px-2 py-1.5"
               data-testid={`secord-qty-warning-${s.sku_id.slice(0, 8)}`}>
            {warning}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// A line "fires" when it actually produced a benefit — not merely when a
// scheme matched (a matched scheme below its own qty threshold still shows
// scheme_id with nothing to give). `free_item_note` is the plain-text
// giveaway reward (a coupon, a bottle, a free packet) — not inventory
// tracked, so it never has a free_qty/discount_amount of its own; without
// checking benefit_type too, this class of scheme was invisible everywhere
// in the app despite correctly firing server-side.
const lineFired = (l) => (l.discount_amount || 0) > 0 || (l.free_qty_pcs || 0) > 0
  || l.benefit_type === "free_item_note";

// A free_item_note's benefit_value carries its count one of two ways (see
// routes_orders.py::_apply_scheme_to_line): "N × <label>" — the multiplier
// prefix used when the free-item text has no leading number of its own,
// e.g. a fixed per-packet value like "Rs.100/- Free Pkt" — or "<N> <label>"
// for a quantity-based giveaway (e.g. "12 Kg Jeera") where the backend has
// already scaled the leading number by how many times the threshold was
// hit, so that number IS the count. Plain text with neither shape implies
// 1. Splitting count from label and grouping lets the review screen show
// one running total per distinct giveaway instead of the rep having to add
// up separate lines themselves.
function freeItemTotals(lines) {
  const totals = new Map();
  for (const l of lines) {
    if (l.benefit_type !== "free_item_note" || !l.benefit_value) continue;
    const byTimes = /^(\d+)\s*×\s*(.+)$/.exec(l.benefit_value);
    const byLeadingNumber = !byTimes && /^([\d.]+)\s+(.+)$/.exec(l.benefit_value);
    const count = byTimes ? Number(byTimes[1]) : byLeadingNumber ? Number(byLeadingNumber[1]) : 1;
    const label = byTimes ? byTimes[2] : byLeadingNumber ? byLeadingNumber[2] : l.benefit_value;
    totals.set(label, (totals.get(label) || 0) + count);
  }
  return [...totals.entries()];
}

function BasketRollup({ preview }) {
  // Phase 29.4.1 · client-side aggregate over the existing per-line
  // `/orders/scheme-preview` payload. NO new backend endpoint, NO new
  // scheme logic — pure sum + dedupe.
  const discount = preview.totals?.scheme_discount || 0;
  const free = preview.totals?.free_qty_kg || 0;
  const firedLines = (preview.lines || []).filter((l) => l.scheme_id && lineFired(l));
  const schemeIds = new Set(firedLines.map((l) => l.scheme_id));
  const count = schemeIds.size;
  const freeItemNotes = [...new Set(firedLines
    .filter((l) => l.benefit_type === "free_item_note" && l.benefit_value)
    .map((l) => l.benefit_value))];
  // Zero-state: hide entirely.
  if (discount === 0 && free === 0 && freeItemNotes.length === 0) return null;
  return (
    <Card className="border-[#E5197F]/30 bg-pink-50 rounded-2xl"
          data-testid="secord-basket-scheme-rollup">
      <CardContent className="p-3 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-[#E5197F]" />
        <div className="text-sm text-neutral-900">
          You'll get{" "}
          {/* only the benefits that actually fire — no "₹0 off" / "0 kg free" filler */}
          {[
            discount > 0 && (
              <span key="d"><b data-testid="secord-basket-scheme-rollup-discount-amt">{INR.format(discount)}</b> off</span>
            ),
            free > 0 && (
              <span key="f"><b data-testid="secord-basket-scheme-rollup-free-qty">{free}</b> kg free</span>
            ),
            freeItemNotes.length > 0 && (
              <b key="n" data-testid="secord-basket-scheme-rollup-free-items">{freeItemNotes.join(", ")}</b>
            ),
          ].filter(Boolean).reduce((acc, el, i) => (i === 0 ? [el] : [...acc, " + ", el]), [])}
          {" "}total across{" "}
          <b data-testid="secord-basket-scheme-rollup-scheme-count">{count}</b>
          {" "}scheme{count === 1 ? "" : "s"}.
        </div>
      </CardContent>
    </Card>
  );
}

function SchemePanel({ preview }) {
  const withSchemes = preview.lines.filter((l) => l.scheme_id && lineFired(l));
  const free = preview.totals.free_qty_kg || 0;
  const discount = preview.totals.scheme_discount || 0;
  return (
    <Card className="border-amber-200 bg-amber-50 rounded-2xl"
          data-testid="secord-scheme-panel">
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <Sparkles className="h-4 w-4" /> Live scheme preview
        </div>
        <div className="text-xs text-amber-800">
          Gross <b>{INR.format(preview.totals.gross || 0)}</b>
          {discount > 0 && <> · Scheme discount <b>{INR.format(discount)}</b></>}
          {free > 0 && <> · Free <b>{free} kg</b></>}
        </div>
        {withSchemes.length === 0 && (
          <div className="text-[11px] text-amber-700">
            No schemes fire at current quantities.
          </div>
        )}
        {withSchemes.map((l) => (
          <div key={l.sku_id}
               className="text-[11px] text-amber-900 flex justify-between"
               data-testid={`secord-scheme-line-${l.sku_id.slice(0, 8)}`}>
            <span>{l.scheme_code} · {l.scheme_type}</span>
            <span>
              {l.free_qty_kg > 0 && `+${l.free_qty_kg} kg free`}
              {l.discount_amount > 0 && `−${INR.format(l.discount_amount)}`}
              {l.benefit_type === "free_item_note" && `🎁 ${l.benefit_value}`}
              {l.gap_to_next_slab_qty
                ? ` (${l.gap_to_next_slab_qty} to slab ${l.next_slab_qty})`
                : ""}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
